import { useEffect, useRef, useState } from 'react'
import './ZoomBox.css'

function ZoomBox({ videoRef, imageRef, useSMPTE = false, seekTime = 0, is2X = false, onToggle2X }) {
  const canvasRef = useRef(null)
  const containerRef = useRef(null)
  const animationFrameRef = useRef(null)
  const [isActive, setIsActive] = useState(true)
  const [canvasSize, setCanvasSize] = useState({ width: 0, height: 0 })
  const lastSeekTimeRef = useRef(seekTime)
  const lastFrameTimeRef = useRef(0)
  const tempCanvasRef = useRef(null)
  const TARGET_FPS = 30
  const FRAME_INTERVAL = 1000 / TARGET_FPS

  // Resize canvas to fit container
  useEffect(() => {
    const container = containerRef.current
    const canvas = canvasRef.current
    if (!container || !canvas) return

    const resizeCanvas = () => {
      const rect = container.getBoundingClientRect()
      const headerHeight = 60
      const infoHeight = 30
      const availableHeight = rect.height - headerHeight - infoHeight
      const availableWidth = rect.width - 32
      
      const newWidth = Math.floor(availableWidth)
      const newHeight = Math.floor(Math.max(200, availableHeight))
      
      // Only update if size changed
      if (canvas.width !== newWidth || canvas.height !== newHeight) {
        canvas.width = newWidth
        canvas.height = newHeight
        setCanvasSize({ width: newWidth, height: newHeight })
      }
    }

    resizeCanvas()
    const resizeObserver = new ResizeObserver(resizeCanvas)
    resizeObserver.observe(container)

    return () => resizeObserver.disconnect()
  }, [])

  useEffect(() => {
    const canvas = canvasRef.current
    const video = useSMPTE ? null : videoRef.current
    const image = useSMPTE ? imageRef.current : null
    const source = useSMPTE ? image : video
    if (!canvas || !source) return

    if (!tempCanvasRef.current) {
      tempCanvasRef.current = document.createElement('canvas')
    }

    lastSeekTimeRef.current = seekTime

    const ctx = canvas.getContext('2d', {
      alpha: false,
      desynchronized: true,
      willReadFrequently: false
    })
    const width = canvas.width
    const height = canvas.height

    const drawZoomBox = (currentTime = performance.now()) => {
      const seekChanged = seekTime !== lastSeekTimeRef.current
      const isVideoReady = !useSMPTE && video && video.readyState >= 2 && !video.paused
      const isImageReady = useSMPTE && image && image.complete
      const shouldUpdate = isActive && (isVideoReady || isImageReady || seekChanged)

      const timeSinceLastFrame = currentTime - lastFrameTimeRef.current
      const shouldDraw = shouldUpdate && (seekChanged || timeSinceLastFrame >= FRAME_INTERVAL)

      if (!shouldDraw) {
        animationFrameRef.current = requestAnimationFrame(drawZoomBox)
        return
      }

      if (seekChanged) {
        lastSeekTimeRef.current = seekTime
      }
      lastFrameTimeRef.current = currentTime

      // Clear canvas
      ctx.fillStyle = '#000'
      ctx.fillRect(0, 0, width, height)

      // Get source dimensions
      const sourceWidth = useSMPTE ? (image.naturalWidth || 640) : (video.videoWidth || 640)
      const sourceHeight = useSMPTE ? (image.naturalHeight || 360) : (video.videoHeight || 360)

      const tempCanvas = tempCanvasRef.current
      if (tempCanvas.width !== sourceWidth || tempCanvas.height !== sourceHeight) {
        tempCanvas.width = sourceWidth
        tempCanvas.height = sourceHeight
      }

      const tempCtx = tempCanvas.getContext('2d', { willReadFrequently: false })
      tempCtx.drawImage(source, 0, 0, sourceWidth, sourceHeight)
      const imageData = tempCtx.getImageData(0, 0, sourceWidth, sourceHeight)
      const data = imageData.data

      // Calculate center cut area (2x zoom)
      const centerX = Math.floor(sourceWidth / 2)
      const centerY = Math.floor(sourceHeight / 2)
      const cutWidth = Math.floor(sourceWidth / 2)
      const cutHeight = Math.floor(sourceHeight / 2)
      const startX = Math.max(0, centerX - cutWidth / 2)
      const startY = Math.max(0, centerY - cutHeight / 2)

      // Calculate uniform scale to maintain aspect ratio
      const videoAspect = cutWidth / cutHeight
      const canvasAspect = width / height
      
      let displayWidth, displayHeight, offsetX, offsetY
      
      if (canvasAspect > videoAspect) {
        // Canvas is wider - fit to height
        displayHeight = height
        displayWidth = height * videoAspect
        offsetX = (width - displayWidth) / 2
        offsetY = 0
      } else {
        // Canvas is taller - fit to width
        displayWidth = width
        displayHeight = width / videoAspect
        offsetX = 0
        offsetY = (height - displayHeight) / 2
      }

      // Create zoomed image data
      const zoomImageData = ctx.createImageData(width, height)
      const zoomData = zoomImageData.data

      // Uniform scale factor for 2x zoom (maintains aspect ratio)
      const scale = displayWidth / cutWidth

      // Copy and scale center cut with aspect ratio preservation
      for (let y = 0; y < height; y++) {
        for (let x = 0; x < width; x++) {
          const dstIdx = (y * width + x) << 2
          
          // Check if pixel is within the display area
          if (x >= offsetX && x < offsetX + displayWidth && 
              y >= offsetY && y < offsetY + displayHeight) {
            // Map to source coordinates
            const srcX = Math.floor(startX + (x - offsetX) / scale)
            const srcY = Math.floor(startY + (y - offsetY) / scale)

            if (srcX >= 0 && srcX < sourceWidth && srcY >= 0 && srcY < sourceHeight) {
              const srcIdx = (srcY * sourceWidth + srcX) << 2

              zoomData[dstIdx] = data[srcIdx]         // R
              zoomData[dstIdx + 1] = data[srcIdx + 1] // G
              zoomData[dstIdx + 2] = data[srcIdx + 2] // B
              zoomData[dstIdx + 3] = 255              // A
            } else {
              // Black for out of bounds
              zoomData[dstIdx] = 0
              zoomData[dstIdx + 1] = 0
              zoomData[dstIdx + 2] = 0
              zoomData[dstIdx + 3] = 255
            }
          } else {
            // Black for areas outside display
            zoomData[dstIdx] = 0
            zoomData[dstIdx + 1] = 0
            zoomData[dstIdx + 2] = 0
            zoomData[dstIdx + 3] = 255
          }
        }
      }

      // Draw zoomed image
      ctx.putImageData(zoomImageData, 0, 0)

      // Apply focus peaking (edge detection using Sobel operator) on display area
      const edgeData = new Uint8Array(width * height)
      const sobelX = [-1, 0, 1, -2, 0, 2, -1, 0, 1]
      const sobelY = [-1, -2, -1, 0, 0, 0, 1, 2, 1]

      // Only process edge detection within the display area
      const peakingStartY = Math.max(1, Math.floor(offsetY))
      const peakingEndY = Math.min(height - 1, Math.floor(offsetY + displayHeight))
      const peakingStartX = Math.max(1, Math.floor(offsetX))
      const peakingEndX = Math.min(width - 1, Math.floor(offsetX + displayWidth))

      for (let y = peakingStartY; y < peakingEndY; y++) {
        for (let x = peakingStartX; x < peakingEndX; x++) {
          let gx = 0
          let gy = 0

          // Apply Sobel operator
          for (let ky = -1; ky <= 1; ky++) {
            for (let kx = -1; kx <= 1; kx++) {
              const idx = ((y + ky) * width + (x + kx)) << 2
              const gray = 0.299 * zoomData[idx] + 0.587 * zoomData[idx + 1] + 0.114 * zoomData[idx + 2]
              const kernelIdx = (ky + 1) * 3 + (kx + 1)
              gx += gray * sobelX[kernelIdx]
              gy += gray * sobelY[kernelIdx]
            }
          }

          // Calculate edge magnitude
          const magnitude = Math.sqrt(gx * gx + gy * gy)
          edgeData[y * width + x] = Math.min(255, magnitude)
        }
      }

      // Overlay focus peaking (edges in red/yellow) - optimized
      const threshold = 50 // Adjust for sensitivity
      ctx.globalCompositeOperation = 'screen'

      // Batch draw focus peaking for better performance
      for (let y = peakingStartY; y < peakingEndY; y++) {
        for (let x = peakingStartX; x < peakingEndX; x++) {
          const edgeValue = edgeData[y * width + x]
          if (edgeValue > threshold) {
            const intensity = Math.min(1, (edgeValue - threshold) / (255 - threshold))
            // Red peaking with optimized alpha
            ctx.globalAlpha = intensity * 4
            ctx.fillStyle = '#f00'
            ctx.fillRect(x, y, 1, 1)
          }
        }
      }

      ctx.globalAlpha = 1
      ctx.globalCompositeOperation = 'source-over'

      // Draw center crosshair
      ctx.strokeStyle = '#00ff00'
      ctx.lineWidth = 1
      ctx.setLineDash([4, 4])
      ctx.beginPath()
      ctx.moveTo(width / 2, 0)
      ctx.lineTo(width / 2, height)
      ctx.moveTo(0, height / 2)
      ctx.lineTo(width, height / 2)
      ctx.stroke()
      ctx.setLineDash([])

      // Draw border around display area
      ctx.strokeStyle = '#888'
      ctx.lineWidth = 2
      ctx.strokeRect(offsetX, offsetY, displayWidth, displayHeight)

      animationFrameRef.current = requestAnimationFrame(drawZoomBox)
    }

    drawZoomBox()

    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current)
      }
    }
  }, [videoRef, imageRef, useSMPTE, isActive, seekTime, canvasSize])

  return (
    <div className="zoom-box" ref={containerRef}>
      <div className="zoom-box-header">
        <h2>2x Center Cut Zoom</h2>
        <button
          className="toggle-button"
          onClick={() => setIsActive(!isActive)}
        >
          {isActive ? 'Pause' : 'Resume'}
        </button>
        {onToggle2X && (
          <button
            className={`toggle-button ${is2X ? 'active' : ''}`}
            onClick={onToggle2X}
            title="2X Width (Desktop only)"
          >
            2X
          </button>
        )}
      </div>
      <canvas
        ref={canvasRef}
        className="zoom-box-canvas"
      />
      <div className="zoom-box-info">
        <span>Focus Peaking: Red edges</span>
      </div>
    </div>
  )
}

export default ZoomBox

